import React, { useState, useCallback, useEffect, useRef } from 'react';
import ConfirmDialog from './ui/ConfirmDialog';
import { useDeleteConfirm } from '../hooks/useDeleteConfirm';
import { friendlyDeleteError } from '../utils/deleteError';
import { useExport } from './header/exporters';
import idColumn from './ui/idColumn';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DataTable, { Column } from './DataTable';
import ActivityLog from './ActivityLog';
import Icon from './Icon';
import StatusCard, { StatusCardPriority } from './ui/StatusCard';
import { MetricCard } from './MetricCards';
import SearchableSelect from './ui/SearchableSelect';
import '../styles/components/ActivityTracker.css';
import { loadTasks, updateTask, archiveTask, deleteTask, Task, Priority, TaskStatus, BlockedOn, resolveAssigneeName, resolveAssigneeNames, fmtDueDate, getBlockingTaskIds } from '../services/TasksService';
import { fmtId } from '../utils/jiraId';
import { matchesAssigneeFilters, matchesPriorityFilters, matchesTab } from '../utils/taskFilters';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from './NotificationProvider';
import { recordLabel } from '../utils/recordId';
import { useRecordRefs } from '../utils/recordRefs';
import { usePublishRecordList } from '../hooks/useRecordList';

const PRIORITY_DS: Record<Priority, string> = {
  P0: 'danger',
  P1: 'warning',
  P2: 'info',
  P3: 'neutral',
};

const statusSelectClass: Record<TaskStatus, string> = {
  'Not Started': 's-neutral',
  'In Progress': 's-info',
  'Done': 's-success',
};

// blockedOn is a separate facet from status now (a task can be "In Progress"
// AND blocked at once) — its own compact select, styled independently of the
// status select above. '' means "not blocked" (maps to blockedOn: null).
const BLOCKED_ON_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Not blocked' },
  { value: 'internal', label: 'Blocked (internal)' },
  { value: 'external', label: 'Blocked (external)' },
];
const blockedOnSelectClass: Record<string, string> = {
  '': 's-neutral',
  internal: 's-warning',
  external: 's-danger',
};

const tabs = ['All', 'P0 Critical', 'P1 High', 'P2 Medium', 'P3 Low', 'Done'];
type KanbanColKey = TaskStatus | 'Overdue';
// 'Blocked' is no longer a real status column — a blocked task still lives in
// its real status column (Not Started/In Progress), flagged with a badge the
// same way Overdue is. See the kanban-card-blocked handling below.
const kanbanCols: KanbanColKey[] = ['Not Started', 'In Progress', 'Overdue'];

const KANBAN_COL_DS: Record<KanbanColKey, string> = {
  'Not Started': 'neutral',
  'In Progress': 'info',
  'Overdue': 'danger',
  'Done': 'completed',
};

/** Returns the CSS class that adds a priority-coloured left accent bar to a table row. */
const buildRowClass = (highlightId: string | null) => (t: Task): string => {
  const highlight = highlightId === t.id ? 'at-row-highlighted' : '';
  if (t.status === 'Done') return `priority-row-done ${highlight}`.trim();
  if (t.priority === 'P0') return `priority-row-p0 ${highlight}`.trim();
  if (t.priority === 'P1') return `priority-row-p1 ${highlight}`.trim();
  if (t.priority === 'P2') return `priority-row-p2 ${highlight}`.trim();
  return `priority-row-p3 ${highlight}`.trim();
};

/** Returns the CSS class that adds a priority-coloured left border to a kanban card. */
const getKanbanPriorityClass = (t: Task): string => `kp-${t.priority.toLowerCase()}`;

const KANBAN_STATUS_CARD_PRIORITY: Record<Priority, StatusCardPriority> = {
  P0: 'high',
  P1: 'high',
  P2: 'medium',
  P3: 'low',
};

const ASSIGNEE_TASKS_PER_PAGE = 5;

interface AssigneeBucketCardProps {
  name: string;
  tasks: Task[];
  isOverdue: (t: Task) => boolean;
  openEdit: (t: Task) => void;
  updateStatus: (id: string, status: TaskStatus) => void;
  updateBlockedOn: (id: string, blockedOn: BlockedOn) => void;
  setDeleteConfirm: (t: Task) => void;
}

// A quick per-person overview — not a full breakdown (that lives on their
// Team & Contacts profile). Paginated so someone with a long task list
// doesn't blow out the page height.
function AssigneeBucketCard({ name, tasks, isOverdue, openEdit, updateStatus, updateBlockedOn, setDeleteConfirm }: AssigneeBucketCardProps): React.ReactElement {
  const [page, setPage] = useState(0);
  const done = tasks.filter(t => t.status === 'Done').length;
  const blocked = tasks.filter(t => !!t.blockedOn).length;
  const overdueTasks = tasks.filter(isOverdue).length;
  const pageCount = Math.ceil(tasks.length / ASSIGNEE_TASKS_PER_PAGE);
  const paged = tasks.slice(page * ASSIGNEE_TASKS_PER_PAGE, (page + 1) * ASSIGNEE_TASKS_PER_PAGE);

  return (
    <div className="assignee-bucket">
      <div className="assignee-bucket-header">
        <div className="assignee-bucket-name">
          <div className="assignee-avatar">{name.charAt(0).toUpperCase()}</div>
          <span>{name}</span>
        </div>
        <div className="assignee-bucket-stats">
          <span className="ab-stat ab-stat-total">{tasks.length} tasks</span>
          {done > 0 && <span className="ab-stat ab-stat-done">{done} done</span>}
          {overdueTasks > 0 && <span className="ab-stat ab-stat-overdue">{overdueTasks} overdue</span>}
          {blocked > 0 && <span className="ab-stat ab-stat-blocked">{blocked} blocked</span>}
        </div>
      </div>
      <div className="assignee-bucket-tasks">
        {paged.map(t => (
          <div
            key={t.id}
            className={`ab-task-row ${getKanbanPriorityClass(t)}${isOverdue(t) ? ' kanban-card-overdue' : ''}`}
            onClick={() => openEdit(t)}
          >
            {/* Header line (badge + status + delete) is intentionally separate
                from the name below it — a bucket column is only ~300-350px
                wide, and cramming badge/name/team/due/status/delete onto one
                flex row left no width at all for the name once the others
                took their min-content size (it rendered as a 0px-wide column
                wrapping one character per line). Two lines gives the name the
                row's full width to wrap normally. */}
            <div className="ab-task-header">
              <span className={`ds-badge ds-badge-${PRIORITY_DS[t.priority]}`}>{t.priority}</span>
              <span onClick={e => e.stopPropagation()}>
                <SearchableSelect
                  value={t.status}
                  onChange={value => updateStatus(t.id, value as TaskStatus)}
                  className={`status-select at-status-select-narrow ${statusSelectClass[t.status]}`}
                  options={(['Not Started', 'In Progress', 'Done'] as TaskStatus[]).map(s => ({ value: s, label: s }))}
                />
              </span>
              <span onClick={e => e.stopPropagation()}>
                <SearchableSelect
                  value={t.blockedOn ?? ''}
                  onChange={value => updateBlockedOn(t.id, (value || null) as BlockedOn)}
                  className={`status-select at-status-select-narrow ${blockedOnSelectClass[t.blockedOn ?? '']}`}
                  options={BLOCKED_ON_OPTIONS}
                />
              </span>
              {/* No Edit button: the whole row already calls openEdit, so the
                  pencil was a second control for the same navigation. Delete
                  stays — it is a different action, and this row plus the kanban
                  card are the only per-row ways to archive a task. */}
              <div className="ab-task-actions">
                <button className="at-action-btn at-action-danger" onClick={e => { e.stopPropagation(); setDeleteConfirm(t); }} title={t.archived ? 'Delete forever' : 'Archive'}>
                  <Icon name="trash" size={12} />
                </button>
              </div>
            </div>
            <div className="ab-task-name">{t.task}</div>
            {(t.ownerTeam || t.dueDateISO) && (
              <div className="ab-task-meta">
                {t.ownerTeam && <span className="ab-task-team">{t.ownerTeam}</span>}
                {t.dueDateISO && (
                  <span className={`ab-task-due${isOverdue(t) ? ' overdue-date' : ''}`}>{fmtDueDate(t.dueDateISO)}</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {pageCount > 1 && (
        <div className="ab-bucket-pagination">
          <button className="ab-bucket-page-btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>‹</button>
          <span className="ab-bucket-page-label">{page + 1} / {pageCount}</span>
          <button className="ab-bucket-page-btn" onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={page === pageCount - 1}>›</button>
        </div>
      )}
    </div>
  );
}

interface ActivityTrackerProps {
  filterTab?: string;
  onFilterTabChange?: (tab: string) => void;
  viewMode?: 'list' | 'kanban' | 'assignee';
  onViewModeChange?: (view: 'list' | 'kanban' | 'assignee') => void;
  triggerAddTask?: boolean;
  onAddTaskHandled?: () => void;
  eventFilter?: string;
  myTasksOnly?: boolean;
  /** Multi-select Priority filter (P0..P3). Empty = every priority. */
  priorityFilters?: string[];
  /** Multi-select Assignee filter (names). Empty = everyone. */
  assigneeFilters?: string[];
  statusFilter?: 'active' | 'archived';
  // The task's own workflow status (Not Started/In Progress/Done/Blocked) —
  // distinct from statusFilter above, which is about archived vs active.
  workflowStatusFilter?: string[];
}

const ActivityTracker: React.FC<ActivityTrackerProps> = ({
  filterTab: externalFilterTab,
  onFilterTabChange,
  viewMode: externalViewMode,
  onViewModeChange,
  triggerAddTask,
  onAddTaskHandled,
  eventFilter,
  myTasksOnly,
  priorityFilters,
  assigneeFilters,
  statusFilter = 'active',
  workflowStatusFilter,
}) => {
  // Re-render when the record numbers (TA-012 instead of a uuid) load.
  useRecordRefs();
  const { user } = useAuth();
  const { showError, showSuccess } = useNotifications();

  // Archived tasks are kept in state (not filtered out here) so the Status
  // dropdown (Active/Archived/All) can switch between them client-side
  // without an extra reload — the archived exclusion moved into
  // `filteredTasks` below, gated on `statusFilter`.
  const loadAndSet = useCallback(() => {
    const byEvent = eventFilter ? loadTasks().filter(t => t.eventIds?.includes(eventFilter)) : loadTasks();
    return byEvent.filter(t => t.taskType !== 'run_of_show');
  }, [eventFilter]);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightedTaskId = searchParams.get('task');
  const tableRef = useRef<HTMLDivElement>(null);
  const [tasks, setTasks] = useState<Task[]>(loadAndSet);

  // Pull latest from API on mount
  useEffect(() => {
    import('../services/TasksService').then(({ refreshTasksFromApi }) => {
      refreshTasksFromApi().then(apiTasks => {
        if (apiTasks.length > 0) {
          const byEvent = eventFilter ? apiTasks.filter(t => t.eventIds?.includes(eventFilter)) : apiTasks;
          setTasks(byEvent.filter(t => t.taskType !== 'run_of_show'));
          window.dispatchEvent(new Event('anojeje:tasks-changed'));
        }
      });
    });
  }, [eventFilter]);
  const [internalTab, setInternalTab] = useState('All');
  const activeTab = externalFilterTab ?? internalTab;
  const setActiveTab = (tab: string) => { setInternalTab(tab); onFilterTabChange?.(tab); };
  const [internalViewMode, setInternalViewMode] = useState<'list' | 'kanban' | 'assignee'>('list');
  const viewMode = externalViewMode ?? internalViewMode;
  const setViewMode = (v: 'list' | 'kanban' | 'assignee') => { setInternalViewMode(v); onViewModeChange?.(v); };
  const [deleteConfirm, setDeleteConfirm] = useState<Task | null>(null);
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);

  // A task deep-linked from elsewhere (e.g. the Dashboard) must actually be
  // visible for the highlight/scroll below to do anything — otherwise it's
  // silently a no-op if the tab/view mode happened to be left on something
  // that filters the task out (Kanban/assignee view have no tableRef at all;
  // a priority or Done tab can exclude it outright). Force both to a state
  // that's guaranteed to include the task, once, on arrival.
  useEffect(() => {
    if (!highlightedTaskId) return;
    setViewMode('list');
    // 'All' actually means "not Done" in the filter below — a Done task
    // needs its own tab picked explicitly or it stays invisible regardless.
    const target = tasks.find(t => t.id === highlightedTaskId);
    setActiveTab(target?.status === 'Done' ? 'Done' : 'All');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedTaskId, tasks]);

  useEffect(() => {
    if (!highlightedTaskId) return;
    const el = tableRef.current?.querySelector('.at-row-highlighted');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightedTaskId, tasks, viewMode, activeTab]);

  const refresh = () => setTasks(loadAndSet());

  const updateStatus = (id: string, status: TaskStatus) => {
    // optimistic: cache updated synchronously before API call
    updateTask(id, { status }).catch(error => {
      console.error('Failed to persist task status change', error);
      showError(error?.message || 'Failed to update task status.');
      refresh(); // re-sync in case the cache was reverted (e.g. dependency-blocked)
    });
    refresh();
  };

  // Separate from updateStatus: blockedOn is its own facet now, not a status
  // value, so setting/clearing it never goes through the dependency-blocked
  // guard updateStatus does for ACTIVE_STATUSES transitions.
  const updateBlockedOn = (id: string, blockedOn: BlockedOn) => {
    updateTask(id, { blockedOn }).catch(error => {
      console.error('Failed to persist task blockedOn change', error);
      showError(error?.message || 'Failed to update blocked status.');
      refresh();
    });
    refresh();
  };

  const isOverdue = (t: Task) => {
    if (!t.dueDateISO || t.status === 'Done') return false;
    return new Date(t.dueDateISO) < new Date();
  };

  // Delete forever exists only on an archived task (the server enforces it
  // too); an active one is archived first and can be restored.
  const forever = useDeleteConfirm<Task>({
    record: 'task',
    perform: t => deleteTask(t.id),
    onDone: () => { refresh(); showSuccess('Task deleted.'); },
  });

  const handleDelete = (task: Task) => {
    if (task.archived) { forever.request(task); return; }
    archiveTask(task.id)
      .then(() => { refresh(); })
      .catch(error => {
        console.error('Failed to persist task archive', error);
        showError(friendlyDeleteError(error, { record: 'task' }));
        refresh();
      });
    setDeleteConfirm(null);
  };

  const openAdd = () => { navigate('/task/new'); };
  const openEdit = (task: Task) => { navigate(`/task/${fmtId('TA', task.id)}`); };

  React.useEffect(() => {
    if (triggerAddTask) { openAdd(); onAddTaskHandled?.(); }
  }, [triggerAddTask]); // eslint-disable-line react-hooks/exhaustive-deps

  const PRIORITY_ORDER: Record<string, number> = { P0: 0, P1: 1, P2: 2, P3: 3 };

  const filteredTasks = tasks
    .filter(t => {
      if (statusFilter === 'active' && t.archived) return false;
      if (statusFilter === 'archived' && !t.archived) return false;
      if (workflowStatusFilter && workflowStatusFilter.length > 0 && !workflowStatusFilter.includes(t.status)) return false;
      if (myTasksOnly && !t.assignees?.some(a => resolveAssigneeName(a) === user?.name)) return false;
      if (!matchesAssigneeFilters(t.assignees, assigneeFilters, resolveAssigneeName)) return false;
      if (!matchesPriorityFilters(t, priorityFilters)) return false;
      // The stat cards above the list (Outstanding/Overdue/P0-P1/Blocked) are
      // clickable shortcuts into the same tab mechanism.
      return matchesTab(t, activeTab, { hasStatusFilter: !!workflowStatusFilter && workflowStatusFilter.length > 0, isOverdue });
    })
    .sort((a, b) => {
      const aDate = a.dueDateISO ? new Date(a.dueDateISO).getTime() : Infinity;
      const bDate = b.dueDateISO ? new Date(b.dueDateISO).getTime() : Infinity;
      if (aDate !== bDate) return aDate - bDate;
      return (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9);
    });

  // The rows as shown (filtered, sorted): the task page's previous / next walk this order.
  usePublishRecordList('task', filteredTasks.map(t => t.id));

  // Export button (Header): the rows this list shows right now.
  useExport('tasks', {
    rows: filteredTasks,
    ctx: {
      idLabel: t => recordLabel('task', t.id),
      assignees: t => (t.assignees?.length ? resolveAssigneeNames(t.assignees) : ''),
      due: t => (t.dueDateISO ? fmtDueDate(t.dueDateISO) : ''),
    },
  });

  const statusScopedTasks = tasks.filter(t => {
    if (statusFilter === 'active') return !t.archived;
    if (statusFilter === 'archived') return !!t.archived;
    return true;
  });
  const statsTasks = myTasksOnly
    ? statusScopedTasks.filter(t => t.assignees?.some(a => resolveAssigneeName(a) === user?.name))
    : statusScopedTasks;
  const outstanding = statsTasks.filter(t => t.status !== 'Done').length;
  const overdue = statsTasks.filter(isOverdue).length;
  const blocked = statsTasks.filter(t => !!t.blockedOn).length;
  const p0p1 = statsTasks.filter(t => t.priority === 'P0' || t.priority === 'P1').length;

  const columns: Column<Task>[] = [
    idColumn<Task>('task'),
    {
      key: 'priority',
      header: 'Priority',
      width: '80px',
      align: 'center',
      render: (_, t) => (
        <span className={`ds-badge ds-badge-${PRIORITY_DS[t.priority]}`}>{t.priority}</span>
      ),
    },
    {
      key: 'task',
      header: 'Task',
      // Without a width this column absorbs every remaining pixel, so the notes
      // line stretches the full table. A fixed width rather than a percentage:
      // this same column definition is reused inside the narrow per-assignee
      // cards, where a percentage collapses it to almost nothing.
      width: '320px',
      render: (_, t) => {
        const children = tasks.filter(c => c.parentTaskId === t.id);
        const blocking = getBlockingTaskIds(t);
        return (
          <div className="task-cell dt-link-cell" onClick={() => openEdit(t)}>
            <span className="task-name">
              {t.task}
              {blocking.length > 0 && (
                <span className="at-blocked-badge" title={`Blocked by ${blocking.map(id => recordLabel('task', id)).join(', ')}`}>
                  <Icon name="alert-triangle" size={11} />
                </span>
              )}
              {children.length > 0 && (
                <span className="at-subtask-badge">{children.filter(c => c.status === 'Done').length}/{children.length}</span>
              )}
            </span>
            {t.notes && <span className="task-notes">{t.notes}</span>}
            {t.deliverable && <span className="task-milestone">{t.deliverable}</span>}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '130px',
      render: (_, t) => (
        <span onClick={e => e.stopPropagation()}>
          <SearchableSelect
            value={t.status}
            onChange={value => updateStatus(t.id, value as TaskStatus)}
            className={`status-select ${statusSelectClass[t.status]}`}
            options={(['Not Started', 'In Progress', 'Done'] as TaskStatus[]).map(s => ({ value: s, label: s }))}
          />
        </span>
      ),
    },
    {
      key: 'blockedOn',
      header: 'Blocked',
      width: '150px',
      render: (_, t) => (
        <span onClick={e => e.stopPropagation()}>
          <SearchableSelect
            value={t.blockedOn ?? ''}
            onChange={value => updateBlockedOn(t.id, (value || null) as BlockedOn)}
            className={`status-select ${blockedOnSelectClass[t.blockedOn ?? '']}`}
            options={BLOCKED_ON_OPTIONS}
          />
        </span>
      ),
    },
    {
      key: 'ownerTeam',
      header: 'Team',
      width: '120px',
      render: (_, t) => t.ownerTeam || '—',
    },
    {
      key: 'assignees',
      header: 'Assignee',
      width: '130px',
      render: (_, t) => t.assignees?.length ? resolveAssigneeNames(t.assignees) : '—',
    },
    {
      key: 'dueDate',
      header: 'Due',
      width: '100px',
      // Task has no `dueDate` field (only `dueDateISO`), so the raw-`key`
      // sort fallback always read undefined — sorting silently did nothing.
      sortValue: t => t.dueDateISO ? new Date(t.dueDateISO).getTime() : Infinity,
      render: (_, t) => t.dueDateISO
        ? <span className={isOverdue(t) ? 'overdue-date' : ''}>{fmtDueDate(t.dueDateISO)}</span>
        : '—',
    },
    {
      key: 'milestone',
      header: 'Milestone',
      width: '150px',
      render: (_, t) => t.milestone ? <span className="task-milestone">{t.milestone}</span> : '—',
    },
  ];

  return (
    <div className="wt-container fade-in">
        {/* Stats */}
        <section className="stats-grid">
          <MetricCard
            label="Outstanding" value={outstanding} tone="orange" icon="list"
            active={activeTab === 'Outstanding'}
            onClick={() => setActiveTab(activeTab === 'Outstanding' ? 'All' : 'Outstanding')}
          />
          <MetricCard
            label="Overdue" value={overdue} tone="red" icon="alert-triangle"
            active={activeTab === 'Overdue'}
            onClick={() => setActiveTab(activeTab === 'Overdue' ? 'All' : 'Overdue')}
          />
          <MetricCard
            label="P0 / P1" value={p0p1} tone="orange" icon="flag"
            active={activeTab === 'P0/P1'}
            onClick={() => setActiveTab(activeTab === 'P0/P1' ? 'All' : 'P0/P1')}
          />
          <MetricCard
            label="Blocked" value={blocked} tone="red" icon="alert-triangle"
            active={activeTab === 'Blocked'}
            onClick={() => setActiveTab(activeTab === 'Blocked' ? 'All' : 'Blocked')}
          />
        </section>

        {/* List view */}
        {viewMode === 'list' && (
          <div ref={tableRef}>
            <DataTable
              data={filteredTasks}
              columns={columns}
              hoverable
              compact
              emptyMessage="No tasks found."
              rowClassName={buildRowClass(highlightedTaskId)}
              pagination={{ enabled: true, pageSize: 25, showPageSizeSelector: true, pageSizeOptions: [25, 50, 100] }}
            />
          </div>
        )}

        {/* Assignee view */}
        {viewMode === 'assignee' && (() => {
          const byAssignee = new Map<string, Task[]>();
          filteredTasks.forEach(t => {
            const names: string[] = t.assignees?.length
              ? t.assignees.map(resolveAssigneeName)
              : ['Unassigned'];
            names.forEach(name => {
              if (!byAssignee.has(name)) byAssignee.set(name, []);
              byAssignee.get(name)!.push(t);
            });
          });
          const sorted: [string, Task[]][] = [...byAssignee.entries()].sort(([a], [b]) =>
            a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b)
          );
          return (
            <div className="assignee-buckets">
              {sorted.map(([name, bucketTasks]) => (
                <AssigneeBucketCard
                  key={name}
                  name={name}
                  tasks={bucketTasks}
                  isOverdue={isOverdue}
                  openEdit={openEdit}
                  updateStatus={updateStatus}
                  updateBlockedOn={updateBlockedOn}
                  setDeleteConfirm={setDeleteConfirm}
                />
              ))}
            </div>
          );
        })()}

        {/* Kanban view */}
        {viewMode === 'kanban' && (
          <div className="kanban-board">
            {kanbanCols.map(col => {
              // Overdue column is derived (past due & not done) — not a status drop target
              const isOverdueCol = col === 'Overdue';
              const colTasks = isOverdueCol
                ? filteredTasks.filter(isOverdue)
                : filteredTasks.filter(t => t.status === col && !isOverdue(t));
              return (
                <div
                  key={col}
                  className={`kanban-column${dragOverCol === col && !isOverdueCol ? ' kanban-drag-over' : ''}${isOverdueCol ? ' kanban-column-overdue' : ''}`}
                  onDragOver={e => { e.preventDefault(); if (!isOverdueCol) setDragOverCol(col); }}
                  onDragLeave={() => setDragOverCol(null)}
                  onDrop={() => {
                    // Can't drop onto the Overdue column — it's a derived view
                    if (draggedTaskId && !isOverdueCol) {
                      updateStatus(draggedTaskId, col as TaskStatus);
                      setDraggedTaskId(null);
                      setDragOverCol(null);
                    }
                  }}
                >
                  <div className="kanban-column-header">
                    <span className={`ds-badge ds-badge-${KANBAN_COL_DS[col]}`}>{col}</span>
                    <span className="kanban-count">{colTasks.length}</span>
                    {isOverdueCol && <span className="kanban-col-hint">drag to reassign</span>}
                  </div>
                  <div className="kanban-cards">
                    {colTasks.map(t => {
                      const metaLines: string[] = [];
                      if (isOverdueCol) metaLines.push(`Status: ${t.status}`);
                      if (t.assignees?.length > 0) metaLines.push(`Assignee: ${resolveAssigneeNames(t.assignees)}`);
                      if (t.dueDateISO) metaLines.push(`Due: ${fmtDueDate(t.dueDateISO)}`);
                      const overdueFlag = isOverdue(t);
                      // blockedOn is a facet independent of which real-status
                      // column the card sits in now — no more dedicated
                      // 'Blocked' column, so this flag is computed straight
                      // off the task rather than off the column.
                      const blockedFlag = !overdueFlag && !!t.blockedOn;
                      return (
                        <div
                          key={t.id}
                          className={`kanban-card${draggedTaskId === t.id ? ' kanban-dragging' : ''}${overdueFlag ? ' kanban-card-overdue' : blockedFlag ? ' kanban-card-blocked' : ''}`}
                          draggable
                          onDragStart={() => setDraggedTaskId(t.id)}
                          onDragEnd={() => { setDraggedTaskId(null); setDragOverCol(null); }}
                        >
                          <StatusCard
                            category={t.ownerTeam || 'Task'}
                            priority={KANBAN_STATUS_CARD_PRIORITY[t.priority]}
                            priorityLabel={t.priority}
                            title={t.task}
                            metaLines={metaLines}
                            // Overdue/Blocked badge lives inside the one card (not a
                            // second box around it); the tint is on .sc-card.
                            badge={(overdueFlag || blockedFlag) ? (
                              <span className={`ds-badge ds-badge-${overdueFlag ? 'danger' : 'warning'}`}>
                                {overdueFlag ? 'Overdue' : `Blocked (${t.blockedOn})`}
                              </span>
                            ) : undefined}
                            actions={[
                              { icon: 'edit', title: 'Edit', onClick: () => openEdit(t) },
                              // Delete only ever applies once a task is
                              // archived — TasksService.deleteTask no-ops
                              // otherwise. Active tasks get "Archive" instead,
                              // matching the archive-first pattern elsewhere
                              // (e.g. Trash panel, ClientsView row actions).
                              { icon: 'trash', title: t.archived ? 'Delete forever' : 'Archive', onClick: () => setDeleteConfirm(t) },
                            ]}
                          />
                        </div>
                      );
                    })}
                    {colTasks.length === 0 && (
                      <div className="kanban-empty-col">
                        {isOverdueCol ? '✓ Nothing overdue' : 'No tasks'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {(() => {
        const highlightedTask = highlightedTaskId ? tasks.find(t => t.id === highlightedTaskId) : null;
        const activities = highlightedTask?.activity_log || [];
        return (
          <ActivityLog
            activities={activities}
            title={highlightedTask ? `Activity for "${highlightedTask.task}"` : 'Recent Activity'}
            className="activity-tracker-log"
            defaultCollapsed={activities.length === 0}
          />
        );
      })()}

      <ConfirmDialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        title="Archive this task?"
        subject={deleteConfirm?.task}
        description="It will be hidden from the list but can be restored from the Archived view."
        confirmLabel="Archive"
        variant="primary"
      />
      <ConfirmDialog
        {...forever.dialogProps}
        title="Delete this task forever?"
        subject={forever.target?.task}
        description="This cannot be undone."
        consequences={['The task and its notes', 'Its activity history']}
        confirmLabel="Delete forever"
      />
    </div>
  );
};

// Export loadTasks for Dashboard use (Next Actions widget - issue #4)
export { loadTasks as loadTasksForDashboard };

export default ActivityTracker;
